// admin-notifications.api.ts

import type {
  AdminNotificationItem,
  AdminNotificationMutationResponse,
  AdminNotificationRealtimeEvent,
  AdminNotificationUserStateRow,
  AdminNotificationsFeedFilters,
  AdminNotificationsFeedResult,
  UUID,
} from './admin-notifications.types';

import {
  toAdminNotificationItem,
  toAdminNotificationsFeedRpcInput,
} from './admin-notifications.helpers';

import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
  SupabaseClient,
} from '@supabase/supabase-js';

export interface AdminNotificationsApi {
  getFeed(
    filters?: AdminNotificationsFeedFilters,
  ): Promise<AdminNotificationsFeedResult>;

  getUnreadCount(): Promise<number>;

  dismiss(notificationId: number): Promise<AdminNotificationMutationResponse>;

  markRead(notificationId: number): Promise<AdminNotificationMutationResponse>;

  markUnread(notificationId: number): Promise<AdminNotificationMutationResponse>;

  delete(notificationId: number): Promise<AdminNotificationMutationResponse>;

  subscribeToOwnStateChanges(
    userId: UUID,
    onChange: (event: AdminNotificationRealtimeEvent) => void,
    channelName?: string,
    onStatusChange?: (status: string, error?: Error | null) => void,
  ): RealtimeChannel;
}

function assertRpcOk(error: { message?: string } | null, context: string): void {
  if (error) {
    throw new Error(
      `[admin-notifications:${context}] ${error.message ?? 'Unknown RPC error'}`,
    );
  }
}

function toMutationResponse(data: unknown): AdminNotificationMutationResponse {
  const row = Array.isArray(data) ? data[0] : data;

  if (!row || typeof row !== 'object') {
    throw new Error('[admin-notifications] Invalid mutation response');
  }

  const record = row as Record<string, unknown>;

  return {
    ok: Boolean(record.ok),
    notification_id: Number(record.notification_id),
    state: String(record.state) as AdminNotificationMutationResponse['state'],
  };
}

function mapRealtimePayload(
  payload: RealtimePostgresChangesPayload<AdminNotificationUserStateRow>,
): AdminNotificationRealtimeEvent {
  const kind = payload.eventType as AdminNotificationRealtimeEvent['kind'];

  return {
    kind,
    schema: 'public',
    table: 'admin_notification_user_states',
    record: (payload.new as AdminNotificationUserStateRow | undefined) ?? null,
    oldRecord: (payload.old as AdminNotificationUserStateRow | undefined) ?? null,
  };
}

export function createAdminNotificationsApi<Database = unknown>(
  supabase: SupabaseClient<Database>,
): AdminNotificationsApi {
  return {
    async getFeed(
      filters: AdminNotificationsFeedFilters = {},
    ): Promise<AdminNotificationsFeedResult> {
      const rpcInput = toAdminNotificationsFeedRpcInput(filters);
      const normalizedLimit = Math.min(Math.max(Math.trunc(filters.limit ?? 15), 1), 100);

      const { data, error } = await supabase.rpc(
        'rpc_admin_notifications_feed_v2',
        rpcInput as Record<string, unknown>,
      );

      assertRpcOk((error as { message?: string } | null) ?? null, 'getFeed');

      const rows = Array.isArray(data) ? data : [];
      const items: AdminNotificationItem[] = rows.map((row) =>
        toAdminNotificationItem(row),
      );
      const lastItem = items.at(-1);
      const hasMore = items.length === normalizedLimit;

      return {
        items,
        count: items.length,
        unreadCountInPage: items.filter((item) => item.isUnread).length,
        hasMore,
        nextCursorCreatedAt: hasMore ? (lastItem?.createdAt ?? null) : null,
        nextCursorNotificationId: hasMore ? (lastItem?.id ?? null) : null,
      };
    },

    async getUnreadCount(): Promise<number> {
      const { data, error } = await supabase.rpc(
        'rpc_admin_notifications_unread_count_v2',
      );

      assertRpcOk(error, 'getUnreadCount');

      const count = Array.isArray(data)
        ? Number((data[0] as Record<string, unknown> | undefined)?.count ?? 0)
        : typeof data === 'object' && data !== null
          ? Number((data as Record<string, unknown>).count ?? 0)
          : Number(data ?? 0);

      return Number.isFinite(count) ? count : 0;
    },

    async dismiss(notificationId: number): Promise<AdminNotificationMutationResponse> {
      const { data, error } = await supabase.rpc(
        'rpc_admin_notification_dismiss',
        {
          p_notification_id: notificationId,
        },
      );

      assertRpcOk(error, 'dismiss');
      return toMutationResponse(data);
    },

    async markRead(notificationId: number): Promise<AdminNotificationMutationResponse> {
      const { data, error } = await supabase.rpc(
        'rpc_admin_notification_mark_read',
        {
          p_notification_id: notificationId,
        },
      );

      assertRpcOk(error, 'markRead');
      return toMutationResponse(data);
    },

    async markUnread(notificationId: number): Promise<AdminNotificationMutationResponse> {
      const { data, error } = await supabase.rpc(
        'rpc_admin_notification_mark_unread',
        {
          p_notification_id: notificationId,
        },
      );

      assertRpcOk(error, 'markUnread');
      return toMutationResponse(data);
    },

    async delete(notificationId: number): Promise<AdminNotificationMutationResponse> {
      return this.dismiss(notificationId);
    },

    subscribeToOwnStateChanges(
      userId: UUID,
      onChange: (event: AdminNotificationRealtimeEvent) => void,
      channelName = `admin-notifications-user-states-${userId}`,
      onStatusChange,
    ): RealtimeChannel {
      return supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'admin_notification_user_states',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            onChange(
              mapRealtimePayload(
                payload as RealtimePostgresChangesPayload<AdminNotificationUserStateRow>,
              ),
            );
          },
        )
        .subscribe((status, error) => {
          onStatusChange?.(status, error ?? null);
        });
    },
  };
}